class SearchAPI extends HTMLElement {
  constructor() {
    super();
    this.API_BASE_URL = "https://services.mybcapps.com/bc-sf-filter/search";
    // Get shop domain from data attribute or use default
    this.SHOP_DOMAIN = this.getAttribute("data-shop-domain") || "elie-tahari.myshopify.com";
    this.SEARCH_INPUT_ID = "searchresults";
    this.SEARCH_RESULTS_GRID_ATTR = "grid-search";
    this.PRODUCTS_PER_PAGE = 24; // Number of products to show initially and per "View More" click
    this.allProductHandles = []; // Store all product handles from search
    this.currentDisplayCount = 0; // Track how many products are currently displayed
    this.totalProducts = 0; // Total number of products found
    this.currentSearchTerm = ''; // Store current search term for filter updates
    this.currentFilters = {}; // Store current applied filters
    this.currentPage = 1; // Track current page for pagination
    this.hasMorePages = true; // Track if there are more pages to load
    this.filterBaseUrl = ''; // Store the filter URL for pagination
    this.init();
  }

  init() {
    this.setupSearchForm();
    this.setupGrid();
    this.setupFilterListeners();
    this.checkUrlParams();
  }

  /**
   * Check URL parameters on page load and auto-trigger search if query exists
   */
  checkUrlParams() {
    // Get query parameter from URL
    const urlParams = new URLSearchParams(window.location.search);
    const query = urlParams.get('q');
    const sortParam = urlParams.get('sort');
    
    // If sort parameter exists in URL, store it in filters
    if (sortParam) {
      // Map sort values to API format
      const sortMap = {
        'price-ascending': 'price-ascending',
        'price-descending': 'price-descending',
        'best-selling': 'best-selling',
        'created-descending': 'created-descending',
        'date-descending': 'created-descending',
        'title-ascending': 'title-ascending',
        'title-descending': 'title-descending',
        'manual': 'manual',
        'featured': 'manual',
        'relevance': 'relevance'
      };
      
      const apiSortValue = sortMap[sortParam] || sortParam;
      this.currentFilters.sort = apiSortValue;
      console.log('[Search API] Sort from URL:', sortParam, '-> API value:', apiSortValue);
    }
    
    if (query && query.trim().length > 0) {
      // Store search term immediately
      this.currentSearchTerm = query.trim();
      
      // Update heading immediately with search term from URL
      this.updateSearchHeading(query.trim());
      
      // Wait for DOM to be fully ready
      const checkAndSearch = () => {
        const searchInput = document.getElementById(this.SEARCH_INPUT_ID);
        const grid = document.querySelector(`[${this.SEARCH_RESULTS_GRID_ATTR}]`);
        
        if (searchInput && grid) {
          // Set the input value to match the URL parameter (if not already set)
          if (!searchInput.value || searchInput.value.trim() !== query.trim()) {
            searchInput.value = query.trim();
          }
          
          // If sort parameter exists, use performSearchWithFilters instead of performSearch
          if (sortParam && this.currentFilters.sort) {
            this.performSearchWithFilters(query.trim(), this.currentFilters);
          } else {
            // Trigger search
            this.performSearch(query.trim());
          }
        } else {
          // Retry after a short delay if elements aren't ready yet
          setTimeout(checkAndSearch, 50);
        }
      };
      
      // Start checking after a brief delay to ensure DOM is ready
      if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', checkAndSearch);
      } else {
        setTimeout(checkAndSearch, 100);
      }
    }
  }

  setupSearchForm() {
    const searchInput = document.getElementById(this.SEARCH_INPUT_ID);
    if (!searchInput) {
      console.warn(`[Search API] Search input with id="${this.SEARCH_INPUT_ID}" not found.`);
      return;
    }

    // Check if search-api grid exists, if not, don't interfere with default search
    const grid = document.querySelector(`[${this.SEARCH_RESULTS_GRID_ATTR}]`);
    if (!grid) {
      // Grid not found, let default search work
      return;
    }

    // Find the form containing the search input
    const searchForm = searchInput.closest("form");
    if (!searchForm) {
      console.warn(`[Search API] Form containing search input not found.`);
      return;
    }

    // Prevent default form submission only if we have the API grid
    searchForm.addEventListener("submit", (e) => {
      e.preventDefault();
      const searchTerm = searchInput.value.trim();
      if (searchTerm) {
        this.performSearch(searchTerm);
      }
    });

    // Also listen for Enter key on the input
    searchInput.addEventListener("keydown", (e) => {
      if (e.key === "Enter") {
        e.preventDefault();
        const searchTerm = searchInput.value.trim();
        if (searchTerm) {
          this.performSearch(searchTerm);
        }
      }
    });
  }

  setupGrid() {
    const grid = document.querySelector(`[${this.SEARCH_RESULTS_GRID_ATTR}]`);
    if (grid) {
      // Hide empty state initially
      const emptyState = document.querySelector(".search-grid--empty-list");
      if (emptyState) {
        emptyState.style.display = "none";
      }
    }
  }

  /**
   * Setup listeners for Boost PFS filter changes
   */
  setupFilterListeners() {
    // Handle sort button clicks with capture phase to ensure we catch them before Boost PFS
    // ONLY on search pages - check if sort button is in search drawer
    document.addEventListener('click', (e) => {
      // Check for sort button clicks first
      const sortBtn = e.target.closest('.boost-pfs-filter-top-sorting .Popover__Value');
      if (!sortBtn) {
        // Not a sort button click, let other handlers process it
        return;
      }
      
      // Check if this sort button is within the search drawer (not collection drawer)
      const sortContainer = sortBtn.closest('.boost-pfs-filter-top-sorting');
      const isInSearchDrawer = sortContainer && (
        sortContainer.closest('#search-filter-drawer') ||
        sortContainer.closest('.search-filter-drawer')
      );
      
      // Also check if we're on a search page by URL
      const isSearchPageByUrl = window.location.pathname.includes('/search');
      
      // Only handle if it's in search drawer OR we're on search page
      if (!isInSearchDrawer && !isSearchPageByUrl) {
        // This is a collection page sort button, let collection page handle it
        return;
      }
      
      // This is a search page sort button - handle it
      const sortValue = sortBtn.getAttribute('data-value');
      if (sortValue) {
        e.preventDefault();
        e.stopPropagation();
        e.stopImmediatePropagation(); // Prevent Boost PFS from also handling this
        
        // Map sort values to API format
        const sortMap = {
          'price-ascending': 'price-ascending',
          'price-descending': 'price-descending',
          'best-selling': 'best-selling',
          'created-descending': 'created-descending',
          'date-descending': 'created-descending',
          'title-ascending': 'title-ascending',
          'title-descending': 'title-descending',
          'manual': 'manual',
          'featured': 'manual',
          'relevance': 'relevance'
        };
        
        const apiSortValue = sortMap[sortValue] || sortValue;
        
        console.log('[Search API] Sort clicked:', sortValue, '-> API value:', apiSortValue);
        
        // Update active state immediately (sortContainer already defined above)
        if (sortContainer) {
          sortContainer.querySelectorAll('.Popover__Value').forEach(function(btn) {
            btn.classList.remove('is-selected', 'active');
            btn.removeAttribute('aria-selected');
          });
          sortBtn.classList.add('is-selected', 'active');
          sortBtn.setAttribute('aria-selected', 'true');
        }
        
        // Ensure search term is available
        if (!this.currentSearchTerm) {
          // Get search term from URL if not set
          const urlParams = new URLSearchParams(window.location.search);
          const query = urlParams.get('q');
          if (query) {
            this.currentSearchTerm = query.trim();
          }
        }
        
        if (!this.currentSearchTerm) {
          console.warn('[Search API] No search term found for sorting');
          return;
        }
        
        // Extract current filters (preserve existing filters) - use cached if available
        const currentFilters = Object.keys(this.currentFilters).length > 0 
          ? Object.assign({}, this.currentFilters) 
          : this.extractFiltersFromDOM();
        
        // Set sort value
        currentFilters.sort = apiSortValue;
        this.currentFilters = currentFilters;
        
        console.log('[Search API] Applying sort with filters:', this.currentFilters);
        
        // Update URL immediately - use search page format: ?q=term&type=product&sort=value
        if (window.history && window.history.replaceState) {
          const url = new URL(window.location);
          // Preserve existing search parameters
          const query = url.searchParams.get('q') || this.currentSearchTerm || '';
          const type = url.searchParams.get('type') || 'product';
          
          // Remove old sort parameters
          url.searchParams.delete('sort');
          url.searchParams.delete('sort_by');
          
          // Set new sort parameter (only if not manual/featured)
          if (apiSortValue && apiSortValue !== 'manual' && apiSortValue !== 'featured') {
            url.searchParams.set('sort', apiSortValue);
          }
          
          // Ensure q and type parameters are set
          if (query) {
            url.searchParams.set('q', query);
          }
          if (type) {
            url.searchParams.set('type', type);
          }
          
          window.history.replaceState({}, '', url);
          console.log('[Search API] URL updated to:', url.toString());
        }
        
        // Apply sort immediately via API (no delay, no waiting for View Results) - matching collection page
        this.performSearchWithFilters(this.currentSearchTerm, this.currentFilters);
        
        // Return early to prevent other handlers from running
        return;
      }
    }, true); // Use capture phase to catch before Boost PFS
    
    // Use event delegation on document to catch dynamically added filter elements
    document.addEventListener('click', (e) => {
      const target = e.target;
      
      // Handle Apply button clicks within filter options
      if (target.classList.contains('boost-pfs-filter-apply-button')) {
        setTimeout(() => this.onFilterChange(), 100);
      }
      
      // Handle Apply All button click
      if (target.classList.contains('boost-pfs-filter-apply-all-button')) {
        setTimeout(() => this.onFilterChange(), 100);
      }
      
      // Handle View Results button click
      if (target.id === 'search-filter-view-results' || target.closest('#search-filter-view-results')) {
        e.preventDefault();
        e.stopPropagation();
        // Call immediately - View Results should always trigger filter
        this.onFilterChange();
      }
      
      // Handle Clear All button clicks
      if (target.classList.contains('boost-pfs-filter-clear-all')) {
        // Wait longer for Boost PFS to clear all filters and update DOM
        setTimeout(() => this.onFilterChange(), 400);
      }
      
      // Handle individual Clear button clicks (within refine-by items or filter options)
      if (target.classList.contains('boost-pfs-filter-clear') || target.closest('.boost-pfs-filter-clear')) {
        // Don't call onFilterChange here - let Boost PFS update DOM first
        // The onFilterChange will be called after a delay to ensure DOM is updated
        setTimeout(() => this.onFilterChange(), 400);
      }
      
      // Handle refine-by item link clicks (the entire refine-by-item is clickable)
      // This should trigger filter update but NOT close the drawer
      const refineByItemLink = target.closest('.refine-by-item a[tabindex="0"]');
      if (refineByItemLink && !target.closest('.boost-pfs-filter-clear')) {
        // Prevent default navigation
        e.preventDefault();
        e.stopPropagation();
        // Wait longer for Boost PFS to update the DOM, then update filters
        setTimeout(() => this.onFilterChange(), 400);
        return;
      }
      
      // Handle refine-by item removal (clicking on applied filter tags)
      if (target.closest('.refine-by-item')) {
        // Check if it's the clear button - if so, we already handled it above
        const isClearButton = target.classList.contains('boost-pfs-filter-clear') || target.closest('.boost-pfs-filter-clear');
        if (!isClearButton) {
          // It's the refine-by-item link itself
          const refineByItem = target.closest('.refine-by-item');
          if (refineByItem) {
            // Wait longer for Boost PFS to update the DOM
            setTimeout(() => this.onFilterChange(), 400);
          }
        }
      }
      
      // Handle Show Results button click (mobile)
      if (target.classList.contains('boost-pfs-filter-show-result')) {
        setTimeout(() => this.onFilterChange(), 100);
      }
    });

    // Also observe for DOM changes to detect when filters are modified by Boost PFS
    this.observeFilterChanges();
  }

  /**
   * Observe filter tree for changes (when Boost PFS updates the filter state)
   */
  observeFilterChanges() {
    // Create a debounced version of filter change handler
    let filterChangeTimeout = null;
    
    const debouncedFilterChange = () => {
      if (filterChangeTimeout) {
        clearTimeout(filterChangeTimeout);
      }
      filterChangeTimeout = setTimeout(() => {
        // Only trigger if we have a search term
        if (this.currentSearchTerm) {
          const newFilters = this.extractFiltersFromDOM();
          const filtersChanged = JSON.stringify(newFilters) !== JSON.stringify(this.currentFilters);
          if (filtersChanged) {
            this.currentFilters = newFilters;
          }
        }
      }, 200);
    };

    // Try to observe the filter tree, retry if not found (Boost PFS loads dynamically)
    const setupObserver = () => {
      const filterTree = document.querySelector('.boost-pfs-filter-tree');
      if (filterTree && filterTree.children.length > 0) {
        const observer = new MutationObserver((mutations) => {
          // Check if the mutation involves selected items
          const hasSelectionChange = mutations.some(mutation => {
            return mutation.type === 'attributes' && mutation.attributeName === 'class';
          });
          
          if (hasSelectionChange) {
            debouncedFilterChange();
          }
        });
        
        observer.observe(filterTree, {
          attributes: true,
          subtree: true,
          attributeFilter: ['class']
        });
      } else {
        // Retry after a delay if filter tree is not ready
        setTimeout(setupObserver, 500);
      }
    };
    
    // Start observer setup
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => setTimeout(setupObserver, 500));
    } else {
      setTimeout(setupObserver, 500);
    }
  }

  /**
   * Handle filter change - extract filters and re-run search
   */
  onFilterChange() {
    if (!this.currentSearchTerm) {
      // Get search term from URL if not set
      const urlParams = new URLSearchParams(window.location.search);
      const query = urlParams.get('q');
      if (query) {
        this.currentSearchTerm = query.trim();
      }
    }
    
    if (!this.currentSearchTerm) {
      return;
    }
    
    // Wait for Boost PFS to fully update the DOM after filter removal
    // Use requestAnimationFrame to wait for DOM updates, then extract filters
    requestAnimationFrame(() => {
      setTimeout(() => {
        // Extract current filters from DOM (after Boost PFS has updated it)
        const newFilters = this.extractFiltersFromDOM();
        
        // Always update and perform filter (don't check if changed, as DOM might not be fully updated yet)
        this.currentFilters = newFilters;
        
        console.log('[Search API] Filters after change:', this.currentFilters);
        
        // Perform search with filters
        this.performSearchWithFilters(this.currentSearchTerm, this.currentFilters);
      }, 300); // Wait for Boost PFS to update DOM
    });
  }

  /**
   * Extract selected filters from the Boost PFS filter DOM
   * @returns {Object} - Object containing all selected filter values
   */
  extractFiltersFromDOM() {
    const filters = {};
    
    // Get all filter options in the filter tree
    const filterOptions = document.querySelectorAll('.boost-pfs-filter-tree .boost-pfs-filter-option');
    
    filterOptions.forEach(option => {
      // Get the filter title/label to determine filter type
      const titleEl = option.querySelector('.boost-pfs-filter-option-title-text');
      if (!titleEl) return;
      
      const filterLabel = titleEl.textContent.trim().toLowerCase();
      
      // Find selected items - Boost PFS adds 'selected' class to li elements
      const selectedItems = option.querySelectorAll('li.selected, .boost-pfs-filter-option-item.selected');
      if (selectedItems.length === 0) return;
      
      const values = [];
      selectedItems.forEach(item => {
        // Try multiple selectors for the value
        const valueEl = item.querySelector('.boost-pfs-filter-option-value') || 
                       item.querySelector('.boost-pfs-filter-button') ||
                       item.querySelector('[data-value]');
        if (valueEl) {
          const value = valueEl.getAttribute('data-value') || valueEl.textContent.trim();
          if (value) values.push(value);
        }
      });
      
      if (values.length === 0) return;
      
      // Map filter labels to API parameter names
      if (filterLabel.includes('product type') || filterLabel.includes('product_type') || filterLabel.includes('type')) {
        filters.product_type = values;
      } else if (filterLabel.includes('size')) {
        filters.size = values;
      } else if (filterLabel.includes('color') || filterLabel.includes('colour')) {
        filters.color = values;
      } else if (filterLabel.includes('vendor') || filterLabel.includes('brand')) {
        filters.vendor = values;
      } else if (filterLabel.includes('collection')) {
        filters.collection = values;
      } else if (filterLabel.includes('tag')) {
        filters.tag = values;
      }
    });
    
    // Extract Price filter (range)
    const priceOption = document.querySelector('.boost-pfs-filter-option[class*="price"]');
    if (priceOption) {
      const minInput = priceOption.querySelector('.boost-pfs-filter-option-range-amount-min, input[class*="min"]');
      const maxInput = priceOption.querySelector('.boost-pfs-filter-option-range-amount-max, input[class*="max"]');
      
      if (minInput && maxInput) {
        const minVal = minInput.value.replace(/[^0-9.]/g, '');
        const maxVal = maxInput.value.replace(/[^0-9.]/g, '');
        
        if (minVal && maxVal) {
          // Check if these are different from the default range
          const slider = priceOption.querySelector('.noUi-target');
          if (slider) {
            const minHandle = slider.querySelector('.noUi-handle-lower');
            const maxHandle = slider.querySelector('.noUi-handle-upper');
            
            if (minHandle && maxHandle) {
              const ariaMin = parseFloat(minHandle.getAttribute('aria-valuemin'));
              const ariaMax = parseFloat(maxHandle.getAttribute('aria-valuemax'));
              const currentMin = parseFloat(minHandle.getAttribute('aria-valuenow'));
              const currentMax = parseFloat(maxHandle.getAttribute('aria-valuenow'));
              
              // Only add price filter if values are different from min/max range
              if (currentMin > ariaMin || currentMax < ariaMax) {
                filters.price = `${currentMin.toFixed(2)}:${currentMax.toFixed(2)}`;
              }
            }
          } else if (parseFloat(minVal) > 0 || parseFloat(maxVal) < 10000) {
            // Fallback: if slider not found but values exist
            filters.price = `${parseFloat(minVal).toFixed(2)}:${parseFloat(maxVal).toFixed(2)}`;
          }
        }
      }
    }
    
    // Extract sort value if set
    const activeSortBtn = document.querySelector('.boost-pfs-filter-top-sorting .Popover__Value.active, .boost-pfs-filter-top-sorting .Popover__Value[aria-selected="true"], .boost-pfs-filter-top-sorting .Popover__Value.is-selected');
    if (activeSortBtn) {
      const sortValue = activeSortBtn.getAttribute('data-value');
      if (sortValue) {
        filters.sort = sortValue;
      }
    }
    
    // Also check refine-by section for already applied filters
    // This is important because sometimes filters are only shown in refine-by, not in filter tree
    // Check if refine-by section exists and has visible items
    const refineBySection = document.querySelector('.boost-pfs-filter-refine-by');
    const refineByItems = refineBySection ? 
      refineBySection.querySelectorAll('.refine-by-item') : 
      [];
    
    // Filter out hidden items
    const visibleRefineByItems = Array.from(refineByItems).filter(item => {
      return item.offsetParent !== null && 
             item.style.display !== 'none' && 
             !item.hasAttribute('style') || 
             !item.getAttribute('style')?.includes('display: none');
    });
    
    visibleRefineByItems.forEach(item => {
      const filterType = item.getAttribute('data-filter-type') || item.getAttribute('data-id');
      const filterValueEl = item.querySelector('.refine-by-type strong');
      const filterValue = filterValueEl ? filterValueEl.textContent.trim() : 
                         (item.getAttribute('data-value') || item.querySelector('a')?.textContent.trim());
      
      // Extract filter type from the refine-by-type span
      const refineByTypeEl = item.querySelector('.refine-by-type span');
      const filterTypeText = refineByTypeEl ? refineByTypeEl.textContent.trim().toLowerCase().replace(':', '') : '';
      
      if (filterValue) {
        // Determine filter type from text or attribute
        let filterKey = null;
        if (filterTypeText.includes('category') || filterTypeText.includes('product type') || filterTypeText.includes('type')) {
          filterKey = 'product_type';
        } else if (filterTypeText.includes('size')) {
          filterKey = 'size';
        } else if (filterTypeText.includes('color') || filterTypeText.includes('colour')) {
          filterKey = 'color';
        } else if (filterTypeText.includes('vendor') || filterTypeText.includes('brand')) {
          filterKey = 'vendor';
        }
        
        // Also check data attributes
        if (!filterKey && filterType) {
          const type = filterType.toLowerCase();
          if (type.includes('product_type') || type.includes('product-type')) {
            filterKey = 'product_type';
          } else if (type.includes('size')) {
            filterKey = 'size';
          } else if (type.includes('color')) {
            filterKey = 'color';
          } else if (type.includes('vendor')) {
            filterKey = 'vendor';
          }
        }
        
        if (filterKey) {
          if (!filters[filterKey]) filters[filterKey] = [];
          if (!filters[filterKey].includes(filterValue)) {
            filters[filterKey].push(filterValue);
          }
        }
      }
    });
    
    console.log('[Search API] Extracted filters:', filters);
    return filters;
  }

  /**
   * Build URL parameters string from filters object
   * @param {Object} filters - Filters object
   * @returns {string} - URL parameters string
   */
  buildFilterParams(filters) {
    const params = [];
    
    // Product Type - pf_pt_product_type[] (pt = product_type)
    if (filters.product_type && filters.product_type.length > 0) {
      filters.product_type.forEach(type => {
        params.push(`pf_pt_product_type[]=${encodeURIComponent(type)}`);
      });
    }
    
    // Size - pf_opt_size[]
    if (filters.size && filters.size.length > 0) {
      filters.size.forEach(size => {
        params.push(`pf_opt_size[]=${encodeURIComponent(size)}`);
      });
    }
    
    // Color - pf_t_color[] (with COLOR_ prefix)
    if (filters.color && filters.color.length > 0) {
      filters.color.forEach(color => {
        // Boost PFS color format requires COLOR_ prefix
        const colorValue = color.startsWith('COLOR_') ? color : 'COLOR_' + color;
        params.push(`pf_t_color[]=${encodeURIComponent(colorValue)}`);
      });
    }
    
    // Vendor - pf_v_vendor[]
    if (filters.vendor && filters.vendor.length > 0) {
      filters.vendor.forEach(vendor => {
        params.push(`pf_v_vendor[]=${encodeURIComponent(vendor)}`);
      });
    }
    
    // Collection
    if (filters.collection && filters.collection.length > 0) {
      filters.collection.forEach(collection => {
        params.push(`pf_c_collection[]=${encodeURIComponent(collection)}`);
      });
    }
    
    // Tag
    if (filters.tag && filters.tag.length > 0) {
      filters.tag.forEach(tag => {
        params.push(`pf_t_tag[]=${encodeURIComponent(tag)}`);
      });
    }
    
    // Price - pf_p_price[]
    if (filters.price) {
      params.push(`pf_p_price[]=${encodeURIComponent(filters.price)}`);
    }
    
    // Sort is NOT included here - it's added in buildFilterUrl (matching collection page)
    
    console.log('[Search API] Filter params:', params.join('&'));
    return params.join('&');
  }

  /**
   * Perform search with applied filters
   * @param {string} searchTerm - Search query
   * @param {Object} filters - Applied filters
   */
  async performSearchWithFilters(searchTerm, filters) {
    const grid = document.querySelector(`[${this.SEARCH_RESULTS_GRID_ATTR}]`);
    if (!grid) {
      return;
    }

    // Update search heading with search term
    this.updateSearchHeading(searchTerm);

    // Reset state for new filter
    this.allProductHandles = [];
    this.currentDisplayCount = 0;
    this.totalProducts = 0;
    this.currentPage = 1;
    this.hasMorePages = true;
    
    // Remove any existing "View More" button
    const existingButton = grid.parentElement.querySelector('.search-view-more-container');
    if (existingButton) {
      existingButton.remove();
    }

    // Show skeleton loader
    this.showSkeletonLoader(grid);

    try {
      // Build filter URL for pagination
      this.buildFilterUrl(searchTerm, filters);
      
      // Fetch only first page (24 products) for fast loading
      const firstPageData = await this.fetchPage(searchTerm, filters, 1);
      
      if (!firstPageData || firstPageData.handles.length === 0) {
        this.hideSkeletonLoader(grid);
        this.showEmptyState(grid);
        this.updateResultCount(0);
        return;
      }

      // Store total products count
      this.totalProducts = firstPageData.totalProducts || 0;
      this.allProductHandles = firstPageData.handles;
      this.currentDisplayCount = 0;
      this.currentPage = 1;
      this.hasMorePages = firstPageData.handles.length === this.PRODUCTS_PER_PAGE && 
                         this.currentDisplayCount + firstPageData.handles.length < this.totalProducts;

      // Update result count
      this.updateResultCount(this.totalProducts);

      // Load initial batch of product cards (first 24 products)
      this.loadProductCards(grid, this.allProductHandles, true);

    } catch (error) {
      console.error("[Search API] Error performing search with filters:", error);
      this.hideSkeletonLoader(grid);
      grid.innerHTML = `<div class="text-center p-5"><p class="text-danger">Error loading search results. Please try again.</p></div>`;
      this.updateResultCount(0);
    }
  }

  /**
   * Build and store filter URL for pagination
   * @param {string} searchTerm - Search query
   * @param {Object} filters - Applied filters
   */
  buildFilterUrl(searchTerm, filters) {
    const filterParams = this.buildFilterParams(filters);
    const sortValue = filters.sort || 'relevance';
    const timestamp = Date.now();
    
    // Build base params matching the Boost PFS search API format
    const baseParams = [
      `t=${timestamp}`,
      `q=${encodeURIComponent(searchTerm)}`,
      `_=pf`,
      `shop=${this.SHOP_DOMAIN}`,
      `limit=${this.PRODUCTS_PER_PAGE}`,
      `display=grid`,
      `collection_scope=0`,
      `event_type=filter`,
      `sort=${encodeURIComponent(sortValue)}`,
      `tag=`,
      `product_available=true`,
      `variant_available=true`,
      `build_filter_tree=true`,
      `check_cache=false`,
      `sort_first=available`
    ].join('&');
    
    this.filterBaseUrl = `${this.API_BASE_URL}?${baseParams}${filterParams ? '&' + filterParams : ''}`;
    console.log('[Search API] Complete Filter URL:', this.filterBaseUrl);
  }

  /**
   * Fetch a single page of products
   * @param {string} searchTerm - Search query
   * @param {Object} filters - Applied filters
   * @param {number} page - Page number to fetch
   * @returns {Promise<Object>} - Object with handles array and totalProducts count
   */
  async fetchPage(searchTerm, filters, page) {
    // Build URL if not already built
    if (!this.filterBaseUrl) {
      this.buildFilterUrl(searchTerm, filters);
    }

    const pageUrl = this.filterBaseUrl + "&page=" + page;

    try {
      const response = await fetch(pageUrl, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
        },
      });

      if (!response.ok) {
        throw new Error("Search request was not ok");
      }

      const data = await response.json();

      // Get total products count
      const totalProducts = typeof data.total_product === "number" ? data.total_product : 0;

      // Extract handles from current page
      const handles = this.extractProductHandles(data);

      return {
        handles: handles,
        totalProducts: totalProducts
      };
    } catch (error) {
      console.error(`[Search API] Error loading page ${page}:`, error);
      return {
        handles: [],
        totalProducts: 0
      };
    }
  }

  /**
   * Fetch all products from all API pages with filters (kept for backward compatibility)
   * @param {string} searchTerm - Search query
   * @param {Object} filters - Applied filters
   * @returns {Promise<Array>} - Array of all product handles
   */
  async fetchAllProductsWithFilters(searchTerm, filters) {
    const filterParams = this.buildFilterParams(filters);
    const sortValue = filters.sort || 'relevance';
    const timestamp = Date.now();
    
    // Build base params matching the working API URL format
    const baseParams = [
      `t=${timestamp}`,
      `q=${encodeURIComponent(searchTerm)}`,
      `_=pf`,
      `shop=${this.SHOP_DOMAIN}`,
      `limit=${this.PRODUCTS_PER_PAGE}`,
      `sort=${sortValue}`,
      `display=grid`,
      `collection_scope=0`,
      `tag=`,
      `product_available=true`,
      `variant_available=true`,
      `build_filter_tree=true`,
      `check_cache=false`,
      `sort_first=available`,
      `event_type=filter`
    ].join('&');
    
    const searchBaseUrl = `${this.API_BASE_URL}?${baseParams}${filterParams ? '&' + filterParams : ''}`;
    
    console.log('[Search API] Filter search URL:', searchBaseUrl);
    
    let page = 1;
    let aggregatedHandles = [];
    let totalProducts = null;
    const MAX_PAGES = 40;

    const loadNextPage = async () => {
      const pageUrl = searchBaseUrl + "&page=" + page;

      try {
        const response = await fetch(pageUrl, {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
          },
        });

        if (!response.ok) {
          throw new Error("Search request was not ok");
        }

        const data = await response.json();

        // Get total products count from first page
        if (typeof totalProducts !== "number") {
          totalProducts = typeof data.total_product === "number" ? data.total_product : 0;
          this.totalProducts = totalProducts;
        }

        // Extract handles from current page
        const pageHandles = this.extractProductHandles(data);
        if (pageHandles.length > 0) {
          aggregatedHandles = aggregatedHandles.concat(pageHandles);
        }

        // Check if we should load more pages
        const shouldLoadMore =
          aggregatedHandles.length < (totalProducts || 0) &&
          pageHandles.length > 0 &&
          page < MAX_PAGES;

        if (shouldLoadMore) {
          page += 1;
          return loadNextPage();
        }

        return aggregatedHandles;
      } catch (error) {
        console.error(`[Search API] Error loading page ${page}:`, error);
        return aggregatedHandles;
      }
    };

    return loadNextPage();
  }

  async performSearch(searchTerm) {
    const grid = document.querySelector(`[${this.SEARCH_RESULTS_GRID_ATTR}]`);
    if (!grid) {
      console.error(`[Search API] Grid with attribute "${this.SEARCH_RESULTS_GRID_ATTR}" not found.`);
      return;
    }

    // Store current search term for filter updates
    this.currentSearchTerm = searchTerm;
    
    // Reset filters for new search
    this.currentFilters = {};

    // Update search heading with search term
    this.updateSearchHeading(searchTerm);

    // Reset state for new search
    this.allProductHandles = [];
    this.currentDisplayCount = 0;
    this.totalProducts = 0;
    this.currentPage = 1;
    this.hasMorePages = true;
    
    // Remove any existing "View More" button
    const existingButton = grid.parentElement.querySelector('.search-view-more-container');
    if (existingButton) {
      existingButton.remove();
    }

    // Show skeleton loader
    this.showSkeletonLoader(grid);

    try {
      // Build filter URL for pagination (no filters)
      this.buildFilterUrl(searchTerm, {});
      
      // Fetch only first page (24 products) for fast loading
      const firstPageData = await this.fetchPage(searchTerm, {}, 1);
      
      if (!firstPageData || firstPageData.handles.length === 0) {
        this.hideSkeletonLoader(grid);
        this.showEmptyState(grid);
        this.updateResultCount(0);
        return;
      }

      // Store total products count
      this.totalProducts = firstPageData.totalProducts || 0;
      this.allProductHandles = firstPageData.handles;
      this.currentDisplayCount = 0;
      this.currentPage = 1;
      this.hasMorePages = firstPageData.handles.length === this.PRODUCTS_PER_PAGE && 
                         this.currentDisplayCount + firstPageData.handles.length < this.totalProducts;

      // Update result count
      this.updateResultCount(this.totalProducts);

      // Load initial batch of product cards (first 24 products)
      this.loadProductCards(grid, this.allProductHandles, true);

    } catch (error) {
      console.error("[Search API] Error performing search:", error);
      this.hideSkeletonLoader(grid);
      grid.innerHTML = `<div class="text-center p-5"><p class="text-danger">Error loading search results. Please try again.</p></div>`;
      this.updateResultCount(0);
    }
  }

  /**
   * Update search heading with search term
   * @param {string} searchTerm - Search query term
   */
  updateSearchHeading(searchTerm) {
    const searchTermDisplays = document.querySelectorAll('.search-term-display, .search-term-display-inline');
    if (searchTermDisplays.length > 0 && searchTerm) {
      const displayText = `"${searchTerm}"`;
      searchTermDisplays.forEach(el => {
        el.textContent = displayText;
      });
      
      // Show the search heading once we have a search term
      const searchHeading = document.querySelector('.main-search-heading');
      if (searchHeading) {
        searchHeading.style.display = '';
      }
    }
  }

  /**
   * Update result count display
   * @param {number} count - Total number of results
   */
  updateResultCount(count) {
    const resultCountEl = document.querySelector('.search-results-count');
    if (resultCountEl) {
      resultCountEl.textContent = count;
      
      // Show the paragraph once we have results
      const searchParagraph = document.querySelector('.main-search-sub-heading');
      if (searchParagraph) {
        searchParagraph.style.display = '';
      }
    }
  }

  /**
   * Show skeleton loader with fading animation
   * @param {HTMLElement} grid - Grid container
   */
  showSkeletonLoader(grid) {
    const skeletonCount = 12; // Show 12 skeleton cards
    let skeletonHTML = '';
    
    for (let i = 0; i < skeletonCount; i++) {
      skeletonHTML += `
        <div class="Grid__Cell 1/2--phone 1/3--tablet-and-up 1/4--desk search-skeleton-item">
          <div class="ProductItem">
            <div class="ProductItem__Wrapper">
              <div class="card-wrapper">
                <div class="ProductItem__ImageWrapper">
                  <div class="AspectRatio AspectRatio--withFallback" style="padding-bottom: 150%; --aspect-ratio: 0.6666666666666666; position: relative;">
                    <div class="search-skeleton-image"></div>
                  </div>
                </div>
                <div class="available-variant-size custom-none">
                  <div class="search-skeleton-sizes"></div>
                </div>
              </div>
              <div class="ProductItem__Info ProductItem__Info--center">
                <div class="ProductItem__TitlePriceRow">
                  <h2 class="ProductItem__Title Heading">
                    <div class="search-skeleton-title"></div>
                  </h2>
                  <div class="ProductItem__PriceList Heading">
                    <div class="search-skeleton-price"></div>
                  </div>
                </div>
                <div class="ProductItem__ColorSwatchRow">
                  <div class="search-skeleton-swatches"></div>
                </div>
              </div>
            </div>
          </div>
        </div>
      `;
    }
    
    grid.innerHTML = skeletonHTML;
    
    // Inject skeleton styles if not already present
    this.injectSkeletonStyles();
  }

  /**
   * Hide skeleton loader
   * @param {HTMLElement} grid - Grid container
   */
  hideSkeletonLoader(grid) {
    const skeletonItems = grid.querySelectorAll('.search-skeleton-item');
    skeletonItems.forEach(item => {
      item.style.opacity = '0';
      item.style.transition = 'opacity 0.3s ease-out';
    });
    
    // Remove skeleton items after fade out
    setTimeout(() => {
      skeletonItems.forEach(item => item.remove());
    }, 300);
  }

  /**
   * Inject skeleton loader CSS styles
   */
  injectSkeletonStyles() {
    // Check if styles are already injected
    if (document.getElementById('search-skeleton-styles')) {
      return;
    }
    
    const style = document.createElement('style');
    style.id = 'search-skeleton-styles';
    style.textContent = `
      .search-skeleton-item {
        animation: searchSkeletonFade 1.5s ease-in-out infinite;
      }
      
      @keyframes searchSkeletonFade {
        0%, 100% {
          opacity: 1;
        }
        50% {
          opacity: 0.6;
        }
      }
      
      .search-skeleton-image {
        position: absolute;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        min-height: 200px;
        background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%);
        background-size: 200% 100%;
        animation: searchSkeletonShimmer 1.5s infinite;
        border-radius: 0;
        z-index: 1;
      }
      
      .search-skeleton-item .AspectRatio {
        position: relative;
        min-height: 200px;
      }
      
      .search-skeleton-item .ProductItem__ImageWrapper {
        display: block;
      }
      
      .search-skeleton-sizes {
        height: 30px;
        margin: 10px 0;
        background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%);
        background-size: 200% 100%;
        animation: searchSkeletonShimmer 1.5s infinite;
        border-radius: 4px;
      }
      
      .search-skeleton-title {
        height: 20px;
        width: 70%;
        margin: 10px 0;
        background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%);
        background-size: 200% 100%;
        animation: searchSkeletonShimmer 1.5s infinite;
        border-radius: 4px;
      }
      
      .search-skeleton-price {
        height: 18px;
        width: 50%;
        margin: 5px 0;
        background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%);
        background-size: 200% 100%;
        animation: searchSkeletonShimmer 1.5s infinite;
        border-radius: 4px;
      }
      
      .search-skeleton-swatches {
        height: 25px;
        width: 60%;
        margin: 10px 0;
        background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%);
        background-size: 200% 100%;
        animation: searchSkeletonShimmer 1.5s infinite;
        border-radius: 4px;
      }
      
      @keyframes searchSkeletonShimmer {
        0% {
          background-position: 200% 0;
        }
        100% {
          background-position: -200% 0;
        }
      }
    `;
    
    document.head.appendChild(style);
  }

  /**
   * Fetch all products from all API pages (similar to custom-search-boost.js)
   * @param {string} searchTerm - Search query
   * @returns {Promise<Array>} - Array of all product handles
   */
  async fetchAllProducts(searchTerm) {
    const searchBaseUrl = `${this.API_BASE_URL}?q=${encodeURIComponent(searchTerm)}&shop=${this.SHOP_DOMAIN}`;
    let page = 1;
    let aggregatedHandles = [];
    let totalProducts = null;
    const MAX_PAGES = 40;

    const loadNextPage = async () => {
      const pageUrl = searchBaseUrl + "&page=" + page;

      try {
        const response = await fetch(pageUrl, {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
          },
        });

        if (!response.ok) {
          throw new Error("Search request was not ok");
        }

        const data = await response.json();

        // Get total products count from first page
        if (typeof totalProducts !== "number") {
          totalProducts = typeof data.total_product === "number" ? data.total_product : 0;
          // Store total products in instance
          this.totalProducts = totalProducts;
        }

        // Extract handles from current page
        const pageHandles = this.extractProductHandles(data);
        if (pageHandles.length > 0) {
          aggregatedHandles = aggregatedHandles.concat(pageHandles);
        }

        // Check if we should load more pages
        const shouldLoadMore =
          aggregatedHandles.length < (totalProducts || 0) &&
          pageHandles.length > 0 &&
          page < MAX_PAGES;

        if (shouldLoadMore) {
          page += 1;
          return loadNextPage();
        }

        return aggregatedHandles;
      } catch (error) {
        console.error(`[Search API] Error loading page ${page}:`, error);
        return aggregatedHandles; // Return what we have so far
      }
    };

    return loadNextPage();
  }

  extractProductHandles(data) {
    const handles = [];
    
    if (data && data.products && Array.isArray(data.products)) {
      data.products.forEach((product) => {
        if (product.handle) {
          handles.push(product.handle);
        }
      });
    }
    
    return handles;
  }

  loadProductCards(grid, handles, isInitialLoad = false) {
    return new Promise((resolve, reject) => {
      const requests = handles.map((handle) => {
        const productTileTemplateUrl = `/products/${handle}?view=search`;
        return fetch(productTileTemplateUrl)
          .then((res) => {
            if (res.status === 200) {
              return res.text();
            }
            return null;
          })
          .catch((error) => {
            console.error(`[Search API] Error loading product ${handle}:`, error);
            return null;
          });
      });

      Promise.all(requests).then((responses) => {
        const validResponses = responses.filter((r) => r !== null);
        
        if (validResponses.length === 0) {
          if (isInitialLoad) {
            this.showEmptyState(grid);
          }
          resolve();
          return;
        }

        const productCards = validResponses.join("");
        
        if (isInitialLoad) {
          // Hide skeleton first, then replace with products
          this.hideSkeletonLoader(grid);
          
          // Wait for skeleton fade out, then show products
          setTimeout(() => {
            grid.innerHTML = productCards;
            this.currentDisplayCount = validResponses.length;
            
            // Show grid, hide empty state
            const emptyState = document.querySelector(".search-grid--empty-list");
            if (emptyState) {
              emptyState.style.display = "none";
            }
            
            // Trigger any necessary initialization for the product cards
            this.initializeProductCards(grid);
            
            // Add or update "View More" button
            this.updateViewMoreButton();
            
            resolve();
          }, 300); // Wait for skeleton fade out animation
        } else {
          // Append to existing content for "View More"
          grid.insertAdjacentHTML('beforeend', productCards);
          this.currentDisplayCount += validResponses.length;
          
          // Show grid, hide empty state
          const emptyState = document.querySelector(".search-grid--empty-list");
          if (emptyState) {
            emptyState.style.display = "none";
          }
          
          // Trigger any necessary initialization for the product cards
          this.initializeProductCards(grid);
          
          // Add or update "View More" button
          this.updateViewMoreButton();
          
          resolve();
        }
      }).catch((error) => {
        console.error('[Search API] Error in loadProductCards:', error);
        reject(error);
      });
    });
  }

  updateViewMoreButton() {
    const grid = document.querySelector(`[${this.SEARCH_RESULTS_GRID_ATTR}]`);
    if (!grid) return;
    
    // Remove existing button container if any
    const existingContainer = grid.parentElement.querySelector('.search-view-more-container');
    if (existingContainer) {
      existingContainer.remove();
    }
    
    // Check if there are more products to show
    let hasMoreProducts = false;
    
    if (this.totalProducts > 0) {
      // We have total count - check if displayed count is less than total
      hasMoreProducts = this.currentDisplayCount < this.totalProducts;
    } else {
      // No total count yet - check if we have more pages
      hasMoreProducts = this.hasMorePages;
    }
    
    // Also verify we have a filter URL to load more
    if (hasMoreProducts && !this.filterBaseUrl) {
      hasMoreProducts = false;
    }
    
    // Final check: hasMorePages must be true to show button
    // This ensures the button is hidden when we've reached the end
    if (hasMoreProducts && !this.hasMorePages) {
      hasMoreProducts = false;
    }
    
    console.log('[Search API] View More button check:', {
      currentDisplayCount: this.currentDisplayCount,
      totalProducts: this.totalProducts,
      allProductHandles: this.allProductHandles.length,
      hasMorePages: this.hasMorePages,
      hasFilterBaseUrl: !!this.filterBaseUrl,
      hasMoreProducts: hasMoreProducts
    });
    
    if (hasMoreProducts) {
      // Create "View More" button
      const buttonContainer = document.createElement('div');
      buttonContainer.className = 'search-view-more-container text-center';
      buttonContainer.style.cssText = '';
      
      const viewMoreButton = document.createElement('button');
      viewMoreButton.className = 'search-view-more-button button button--medium';
      viewMoreButton.textContent = `View More`;
      viewMoreButton.type = 'button';
      
      viewMoreButton.addEventListener('click', () => {
        this.loadMoreProducts();
      });
      
      buttonContainer.appendChild(viewMoreButton);
      grid.parentElement.appendChild(buttonContainer);
      
      console.log('[Search API] View More button created');
    } else {
      console.log('[Search API] View More button hidden - no more products');
    }
  }

  /**
   * Add/remove footer logo loading class
   * @param {boolean} add - If true, add the class; if false, remove it
   */
  toggleFooterLogoLoading(add) {
    const footerLogoImg = document.querySelector(".Footer__Block.Footer__Block--logo img");
    const footerLogoImgview = document.querySelector(".search-view-more-button");
    if (footerLogoImg) {
      if (add) {
        footerLogoImg.classList.add('footer-logo-loading');
         footerLogoImgview.classList.add('footer-logo-loading-new');
      } else {
        footerLogoImg.classList.remove('footer-logo-loading');
        footerLogoImgview.classList.remove('footer-logo-loading-new');
      }
    }
  }

  async loadMoreProducts() {
    const grid = document.querySelector(`[${this.SEARCH_RESULTS_GRID_ATTR}]`);
    if (!grid) return;
    
    const viewMoreButton = grid.parentElement.querySelector('.search-view-more-button');
    let originalText = 'View More';
    
    // Add footer logo loading class
    this.toggleFooterLogoLoading(true);
    
    // Remove footer logo loading class after 2 seconds
    setTimeout(() => {
      this.toggleFooterLogoLoading(false);
    }, 4000);
    
    // Disable button and show loading state
    if (viewMoreButton) {
      viewMoreButton.disabled = true;
       viewMoreButton.style.display = 'none';
      originalText = viewMoreButton.textContent;
      viewMoreButton.textContent = '';
    }
    
    // Check if we have more pages to load
    if (!this.hasMorePages || !this.filterBaseUrl) {
      console.log('[Search API] No more pages to load. hasMorePages:', this.hasMorePages, 'filterBaseUrl:', !!this.filterBaseUrl);
      // Re-enable button and let updateViewMoreButton handle hiding it
      if (viewMoreButton) {
        viewMoreButton.disabled = false;
        viewMoreButton.textContent = originalText;
      }
      this.updateViewMoreButton();
      return;
    }

    try {
      // Fetch next page from API
      const nextPage = this.currentPage + 1;
      console.log('[Search API] Loading page', nextPage, 'of products. Current display count:', this.currentDisplayCount, 'Total products:', this.totalProducts);
      
      const pageData = await this.fetchPage(this.currentSearchTerm, this.currentFilters, nextPage);
      
      if (!pageData || pageData.handles.length === 0) {
        // No more products available
        this.hasMorePages = false;
        console.log('[Search API] No more products to load - received empty page');
        
        // Re-enable button and let updateViewMoreButton hide it
        if (viewMoreButton) {
          viewMoreButton.disabled = false;
          viewMoreButton.textContent = originalText;
        }
        this.updateViewMoreButton();
        return;
      }

      // Add new handles to our collection
      this.allProductHandles = this.allProductHandles.concat(pageData.handles);
      this.currentPage = nextPage;
      
      // Update total products if we got a new value
      if (pageData.totalProducts > 0) {
        this.totalProducts = pageData.totalProducts;
        this.updateResultCount(this.totalProducts);
      }
      
      // Load the new batch of product cards (this will update currentDisplayCount)
      await this.loadProductCards(grid, pageData.handles, false);
      
      // Small delay to ensure DOM is updated
      await new Promise(resolve => setTimeout(resolve, 100));
      
      // After products are loaded, check if there are more pages
      // Calculate based on what we've displayed vs total
      const totalDisplayedAfterLoad = this.currentDisplayCount;
      const hasMoreAfterLoad = this.totalProducts > 0 ? totalDisplayedAfterLoad < this.totalProducts : true;
      
      // Also check if we got a full page (means there might be more)
      const gotFullPage = pageData.handles.length === this.PRODUCTS_PER_PAGE;
      
      // Update hasMorePages flag
      if (this.totalProducts > 0) {
        // We know the total - check if we've displayed less than total
        this.hasMorePages = totalDisplayedAfterLoad < this.totalProducts;
      } else {
        // Don't know total yet - assume more if we got a full page
        this.hasMorePages = gotFullPage;
      }
      
      // Additional check: if we got fewer products than PRODUCTS_PER_PAGE, we've reached the end
      if (pageData.handles.length < this.PRODUCTS_PER_PAGE) {
        this.hasMorePages = false;
        console.log('[Search API] Reached end - got fewer products than expected:', pageData.handles.length, '<', this.PRODUCTS_PER_PAGE);
      }
      
      // Final check: if displayed count equals or exceeds total, no more pages
      if (this.totalProducts > 0 && totalDisplayedAfterLoad >= this.totalProducts) {
        this.hasMorePages = false;
        console.log('[Search API] Reached end - displayed all products:', totalDisplayedAfterLoad, '>=', this.totalProducts);
      }
      
      console.log('[Search API] After loading page', nextPage, ':', {
        loadedThisPage: pageData.handles.length,
        totalDisplayed: totalDisplayedAfterLoad,
        totalProducts: this.totalProducts,
        hasMoreAfterLoad: hasMoreAfterLoad,
        gotFullPage: gotFullPage,
        hasMorePages: this.hasMorePages
      });

      // updateViewMoreButton is already called by loadProductCards, but call it again to ensure button state is correct
      // This will show the button if there are more products, or hide it if all are loaded
      this.updateViewMoreButton();
      
    } catch (error) {
      console.error("[Search API] Error loading more products:", error);
      
      // Re-enable button on error
      if (viewMoreButton) {
        viewMoreButton.disabled = false;
        viewMoreButton.textContent = originalText;
      }
      // Update button to show it again if there are more products
      this.updateViewMoreButton();
    }
  }

  initializeProductCards(grid) {
    // Always dispatch search-results-loaded event to initialize image sliders and other components
    // Use document.dispatchEvent to match the listener in product-item-boost-cart.js
    document.dispatchEvent(new CustomEvent("search-results-loaded"));
  }

  showEmptyState(grid) {
    grid.innerHTML = "";
    const emptyState = document.querySelector(".search-grid--empty-list");
    if (emptyState) {
      emptyState.style.display = "block";
    }
  }
}

customElements.define("search-api", SearchAPI);

// Initialize on DOM ready
document.addEventListener("DOMContentLoaded", function () {
  const searchAPI = document.querySelector("search-api");
  if (!searchAPI) {
    // Create and append the custom element if it doesn't exist
    const searchApiElement = document.createElement("search-api");
    document.body.appendChild(searchApiElement);
  }
  // Note: checkUrlParams is already called in init(), which is called in the constructor
  // So we don't need to call it again here
});

